import { Func } from './types/Func'
import { UnaryFunction } from './types/UnaryFunction'
import { pipeImpl } from './pipeImpl'

/**
 * Type-enforcing left-to-right function composition function.
 * The first parameter can be a function of any arity, but the remaining parameters must be unary functions.
 * The return type of one function must be compatible with the argument of next function in the argument list
 * (i.e. types flow from left-to-right)
 * @returns A function with the arguments of the *first* function in the argument list and a return type of the *last* function in the argument list
 */
export function pipe<TIn extends any[], TOut>(f0: Func<TIn, TOut>): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, T7, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, T7, T8, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, T7, T8, T9, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, T7, T8, T9, T10, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], T1, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, T12, TOut>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  T58,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, T58>,
  f58: UnaryFunction<T58, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  T58,
  T59,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, T58>,
  f58: UnaryFunction<T58, T59>,
  f59: UnaryFunction<T59, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  T58,
  T59,
  T60,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, T58>,
  f58: UnaryFunction<T58, T59>,
  f59: UnaryFunction<T59, T60>,
  f60: UnaryFunction<T60, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  T58,
  T59,
  T60,
  T61,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, T58>,
  f58: UnaryFunction<T58, T59>,
  f59: UnaryFunction<T59, T60>,
  f60: UnaryFunction<T60, T61>,
  f61: UnaryFunction<T61, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  T58,
  T59,
  T60,
  T61,
  T62,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, T58>,
  f58: UnaryFunction<T58, T59>,
  f59: UnaryFunction<T59, T60>,
  f60: UnaryFunction<T60, T61>,
  f61: UnaryFunction<T61, T62>,
  f62: UnaryFunction<T62, TOut>
): Func<TIn, TOut>
export function pipe<
  TIn extends any[],
  T1,
  T2,
  T3,
  T4,
  T5,
  T6,
  T7,
  T8,
  T9,
  T10,
  T11,
  T12,
  T13,
  T14,
  T15,
  T16,
  T17,
  T18,
  T19,
  T20,
  T21,
  T22,
  T23,
  T24,
  T25,
  T26,
  T27,
  T28,
  T29,
  T30,
  T31,
  T32,
  T33,
  T34,
  T35,
  T36,
  T37,
  T38,
  T39,
  T40,
  T41,
  T42,
  T43,
  T44,
  T45,
  T46,
  T47,
  T48,
  T49,
  T50,
  T51,
  T52,
  T53,
  T54,
  T55,
  T56,
  T57,
  T58,
  T59,
  T60,
  T61,
  T62,
  T63,
  TOut
>(
  f0: Func<TIn, T1>,
  f1: UnaryFunction<T1, T2>,
  f2: UnaryFunction<T2, T3>,
  f3: UnaryFunction<T3, T4>,
  f4: UnaryFunction<T4, T5>,
  f5: UnaryFunction<T5, T6>,
  f6: UnaryFunction<T6, T7>,
  f7: UnaryFunction<T7, T8>,
  f8: UnaryFunction<T8, T9>,
  f9: UnaryFunction<T9, T10>,
  f10: UnaryFunction<T10, T11>,
  f11: UnaryFunction<T11, T12>,
  f12: UnaryFunction<T12, T13>,
  f13: UnaryFunction<T13, T14>,
  f14: UnaryFunction<T14, T15>,
  f15: UnaryFunction<T15, T16>,
  f16: UnaryFunction<T16, T17>,
  f17: UnaryFunction<T17, T18>,
  f18: UnaryFunction<T18, T19>,
  f19: UnaryFunction<T19, T20>,
  f20: UnaryFunction<T20, T21>,
  f21: UnaryFunction<T21, T22>,
  f22: UnaryFunction<T22, T23>,
  f23: UnaryFunction<T23, T24>,
  f24: UnaryFunction<T24, T25>,
  f25: UnaryFunction<T25, T26>,
  f26: UnaryFunction<T26, T27>,
  f27: UnaryFunction<T27, T28>,
  f28: UnaryFunction<T28, T29>,
  f29: UnaryFunction<T29, T30>,
  f30: UnaryFunction<T30, T31>,
  f31: UnaryFunction<T31, T32>,
  f32: UnaryFunction<T32, T33>,
  f33: UnaryFunction<T33, T34>,
  f34: UnaryFunction<T34, T35>,
  f35: UnaryFunction<T35, T36>,
  f36: UnaryFunction<T36, T37>,
  f37: UnaryFunction<T37, T38>,
  f38: UnaryFunction<T38, T39>,
  f39: UnaryFunction<T39, T40>,
  f40: UnaryFunction<T40, T41>,
  f41: UnaryFunction<T41, T42>,
  f42: UnaryFunction<T42, T43>,
  f43: UnaryFunction<T43, T44>,
  f44: UnaryFunction<T44, T45>,
  f45: UnaryFunction<T45, T46>,
  f46: UnaryFunction<T46, T47>,
  f47: UnaryFunction<T47, T48>,
  f48: UnaryFunction<T48, T49>,
  f49: UnaryFunction<T49, T50>,
  f50: UnaryFunction<T50, T51>,
  f51: UnaryFunction<T51, T52>,
  f52: UnaryFunction<T52, T53>,
  f53: UnaryFunction<T53, T54>,
  f54: UnaryFunction<T54, T55>,
  f55: UnaryFunction<T55, T56>,
  f56: UnaryFunction<T56, T57>,
  f57: UnaryFunction<T57, T58>,
  f58: UnaryFunction<T58, T59>,
  f59: UnaryFunction<T59, T60>,
  f60: UnaryFunction<T60, T61>,
  f61: UnaryFunction<T61, T62>,
  f62: UnaryFunction<T62, T63>,
  f63: UnaryFunction<T63, TOut>
): Func<TIn, TOut>
export function pipe<TIn extends any[], TOut>(
  o1: Func<TIn, any>,
  ...operations: UnaryFunction<any, any>[]
): Func<TIn, TOut> {
  return pipeImpl(o1, ...operations)
}
